import { useState } from "react";
import { useGradesX, type GradeX, type GradeXFormData } from "@/features/produtos/hooks/useGradesX";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Ban } from "lucide-react";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";

export default function GradeXPage() {
  const { gradesX, isLoading, createGradeX, updateGradeX, deleteGradeX } = useGradesX();

  const [dialogOpen, setDialogOpen]       = useState(false);
  const [editing, setEditing]             = useState<GradeX | null>(null);
  const [inativarId, setInativarId]       = useState<number | null>(null);
  const [formData, setFormData]           = useState<GradeXFormData>({ descricao: "", isInativo: false });
  const [busca, setBusca]                 = useState("");

  const gradesFiltradas = busca
    ? gradesX.filter(g => g.descricao?.toLowerCase().includes(busca.toLowerCase()))
    : gradesX;

  const paginacao = usePaginacaoLocal(gradesFiltradas);

  const handleOpenDialog = (grade?: GradeX) => {
    if (grade) {
      setEditing(grade);
      setFormData({ descricao: grade.descricao ?? "", isInativo: grade.isInativo });
    } else {
      setEditing(null);
      setFormData({ descricao: "", isInativo: false });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editing) {
      await updateGradeX.mutateAsync({ id: editing.id, ...formData });
    } else {
      await createGradeX.mutateAsync(formData);
    }
    setDialogOpen(false);
  };

  if (isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Grade X — Eixo X</h1>
          <p className="text-muted-foreground">Gerencie os valores do eixo X da grade de produtos</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Nova Grade X
        </Button>
      </div>

      <div className="max-w-sm">
        <Input
          placeholder="Buscar por descrição..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Descrição</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginacao.itensPaginados.map((grade) => (
              <TableRow key={grade.id}>
                <TableCell className="font-medium">{grade.descricao || "-"}</TableCell>
                <TableCell className="text-center">
                  <Badge variant={grade.isInativo ? "secondary" : "success"}>
                    {grade.isInativo ? "Inativo" : "Ativo"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(grade)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {!grade.isInativo && (
                      <Button variant="ghost" size="icon" onClick={() => setInativarId(grade.id)}>
                        <Ban className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paginacao.itensPaginados.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                  {busca ? "Nenhuma Grade X encontrada para esta busca." : "Nenhuma Grade X cadastrada"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Grade X" : "Nova Grade X"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição *</Label>
              <Input
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                required
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="isInativo"
                checked={formData.isInativo ?? false}
                onCheckedChange={(checked) => setFormData({ ...formData, isInativo: checked })}
              />
              <Label htmlFor="isInativo">Inativo</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={createGradeX.isPending || updateGradeX.isPending}>
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!inativarId} onOpenChange={() => setInativarId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar Grade X</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja inativar este valor de Grade X?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (inativarId) {
                  deleteGradeX.mutate(inativarId);
                  setInativarId(null);
                }
              }}
            >
              Inativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
